import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { PrimeTemplate } from 'primeng/api';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { nextTick } from 'process';

import { v4 } from 'uuid';
import { ValidatorsPlus } from '../../../common/public-api';
import { LocalStorageService } from '../../../common/services/local-storage.service';
import { ChangeStatusEnum } from '../../../core/enums/change-status.enum';
import { ColumnInfo, FileInfo } from '../../../core/models';
import { FileTransferService } from '../../../core/services/file-transfer.service';
import { UserProfileService } from '../../../core/services/user-profile.service';
import { isNullOrEmpty } from '../../../core/utils/common-functions';
import { UserProfile } from '../../../models/common/user-profile.model';
import { CaseSubmissionType } from '../../../rt-shared/enums/case-submission-type.enum';
import { LocalStorageEnum } from '../../../rt-shared/enums/local-storage.enum';
import { FileService } from '../../../rt-shared/services/file.service';
import { BaseComponent } from '../base.component';
import {
  HintDialogService,
  MessageItem,
} from './../../../core/services/hint-dialog.service';
import { MB } from './../../pipes';
import { FileSizePipe } from './../../pipes/file-size.pipe';

const FILE_EXT_ICON_MAP: Map<string, string> = new Map<string, string>([
  ['pdf', 'icon-pdf'],
  ['doc', 'icon-doc'],
  ['docx', 'icon-doc'],
  ['xls', 'icon-xls'],
  ['xlsx', 'icon-xls'],
  ['txt', 'icon-txt'],
  ['zip', 'icon-zip'],
  ['rar', 'icon-zip'],
  ['msg', 'icon-unknow'],
]);
const IMG_EXT_ICON_MAP: Map<string, string> = new Map<string, string>([
  ['png', 'icon-png'],
  ['jpg', 'icon-jpg'],
  ['gif', 'icon-gif'],
  ['jpeg', 'icon-jpeg'],
]);

const INVALID_FILE_TYPE_MSG = 'The file format should be {fileTypes}.';

const DEFAULT_TRUE_FUNC = () => true;

/**
 * 檔案上傳元件
 *
 * @export
 * @class FileUploadComponent
 * @extends {BaseComponent}
 * @implements {OnInit}
 * @implements {OnChanges}
 */
@Component({
  selector: 'sigv-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  providers: [FileSizePipe],
})
export class FileUploadComponent
  extends BaseComponent
  implements OnInit, OnChanges, AfterViewInit 
  {
  /**
   * 唯讀
   */
  @Input() readonly = false;

  /**
   * 判斷是否顯示資料列 checkbox 方法
   */
  @Input() displayRowChkboxFunc: (row: any) => boolean = DEFAULT_TRUE_FUNC;

  /**
   * 判斷是否顯示資料列編輯按鈕方法
   */
  @Input() displayRowEditBtnFunc: (row: any) => boolean = DEFAULT_TRUE_FUNC;

  /**
   * 檔案列表
   */
  @Input() files: FileDisplayInfo[] = [];

  /**
   * 檔案大小限制(byte)
   *
   * @memberof FileUploadComponent
   */
  @Input() maxFileSize = 20 * MB.unit;

  @Input() hintDialogInputService!: HintDialogService;

  /**
   * 檔案格式限制
   *
   * @memberof FileUploadComponent
   */
  @Input() accepts = [
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.pdf',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.txt',
    '.zip',
    '.msg',
  ];

  /**
   * 是否顯示上傳者
   */
  @Input() showProcessor = false;

  /**
   * 檔案名稱重複錯誤訊息
   */
  @Input() fileNameDuplicateMsg = '';
  @Input() isDefaultFile: boolean = false;
  /**
   * 檔案列表變更事件
   */
  @Output() filesChange = new EventEmitter<FileDisplayInfo[]>();

  /**
   * 檔案列表刪除事件
   */
  @Output() filesDelete = new EventEmitter<FileDisplayInfo[]>();

  /**
   * HTML File Input
   */
  @ViewChild('fileInput') fileInput!: ElementRef;

  /**
   * 注入的 PrimeTemplate 清單
   */
  @ContentChildren(PrimeTemplate) templates!: QueryList<PrimeTemplate>;

  /**
   * 進件種類  DealerWeb / E-submission / 審查線下
   */
  @Input() caseSubmissionType = CaseSubmissionType.Unknow;

  /**
   * 注入的工具列 Template
   */
  toolbarTemplate!: TemplateRef<any>;
  acceptTypes = '';
  showDialog = false;
  fileColumns: ColumnInfo[] = [
    { field: 'fileId', header: 'IMG' },
    { field: 'fileName', header: 'File Name' },
    { field: 'uploadTime', header: 'Upload Date' },
    { field: 'fileSize', header: 'File Size' },
    { field: 'description', header: 'File Description' },
  ];
  selectedFiles: FileDisplayInfo[] = [];
  deletedFiles: FileDisplayInfo[] = [];
  tmpUploadFiles: FileDisplayInfo[] = [];
  displayImages: ImageRawData[] = [];
  /**
   * 是否關閉檔名重複檢核
   */
  isCheckDuplicateFileName = true;
  /** dealer web的上傳等送出案件再進行檢核 */
  isFormDlw = false;
  showGalleria = false;
  currentDisplayImgIdx = 0;
  responsiveOptions = [
    {
      breakpoint: '1500px',
      numVisible: 5,
    },
    {
      breakpoint: '1024px',
      numVisible: 3,
    },
    {
      breakpoint: '768px',
      numVisible: 2,
    },
    {
      breakpoint: '560px',
      numVisible: 1,
    },
  ];
  userNo = '';

  form = new FormGroup<{ files: FormArray<FormControl<string | null>> }>(
    {} as { files: FormArray<FormControl<string | null>> }
  );
  constructor(
    private injector: Injector,
    public userProfileService: UserProfileService,
    private fileTransferService: FileTransferService,
    private fileSizePipe: FileSizePipe,
    public hintDialogService: HintDialogService,
    private fb: FormBuilder,
    private cd: ChangeDetectorRef,
    public _localStorageService: LocalStorageService,
    private fileService: FileService
  ) {
    super(injector);
  }

  //#region Angular lifecycle hooks

  ngOnChanges(changes: SimpleChanges): void {
    this.logger.debug('file upload ngOnChanges', changes['files']);
    if (isNullOrEmpty(changes['files']) === false) {
      this.displayImages = this.getGalleria();
      this.initFormGroup();
    }

    if (isNullOrEmpty(changes['accepts'])) {
      this.acceptTypes = this.accepts.join(', ');
    }
  }

  ngOnInit(): void {
    if (this.showProcessor) {
      this.fileColumns.push({ field: 'processor', header: 'Processor' });
    }
    nextTick(() => {
      this.userNo = (
        this._localStorageService.getItem(
          LocalStorageEnum.USER_INFO_STORAGE_KEY
        ) as UserProfile
      ).userNo;
      this.logger.debug('file-upload', this.userNo);
      if (isNullOrEmpty(this.userNo))
        this.userNo = this.userProfileService.userProfile.userNo ?? '';
    });
  }

  ngAfterViewInit(): void {
    this.templates.forEach((item) => {
      switch (item.getType()) {
        case 'toolbar':
          this.toolbarTemplate = item.template;
          break;
        default:
          break;
      }
    });
    this.userProfileService.userProfile$.subscribe({
      next: (results) => {
        this.userNo = results.userNo ?? '';
        this.logger.debug('getUserInfo======subs', this.userNo);
      },
    });
    this.cd.detectChanges();
  }

  //#endregion Angular lifecycle hooks

  /**
   * 上傳按鈕 Click 事件
   *
   * @param {*} event
   * @memberof FileUploadComponent
   */
  onUploadBtnClick(event: any, documentTypeOptions?: any[]): void {
    const invalidFileInfos: FileInfo[] = [];
    this.logger.debug('onUploadBtnClick', event);
    for (let file of event.target.files) {
      this.logger.debug('onUploadBtnClick file', file);
      const tmpId = v4();
      const tmpFileNameChars = file.name.split('.') as string[];
      const extension = `.${tmpFileNameChars.pop()}`;
      const fileName = tmpFileNameChars.join('');
      const fileInfo: FileDisplayInfo = {
        id: tmpId,
        file,
        fileName,
        extension,
        fileSize: file.size,
        isTemp: true,
        documentType: documentTypeOptions?.at(0)?.value ?? '01',
        status: ChangeStatusEnum.added,
        creator: this.userNo,
      };
      if (this.isDefaultFile) fileInfo.documentType = '03';
      if (
        this.accepts
          .map((x) => x.toUpperCase())
          .includes(fileInfo.extension?.toUpperCase() ?? '') === false
      ) {
        const msg = this.translateService.instant(INVALID_FILE_TYPE_MSG, {
          fileTypes: this.acceptTypes,
        });
        this.hintDialogInputService.showErrorHint(msg + this.accepts.join(','));
        event.target.value = '';
        break;
      }

      if ((fileInfo.fileSize ?? 0) > this.maxFileSize) {
        invalidFileInfos.push(fileInfo);
        continue;
      }

      if (this.checkIsPhoto(fileInfo)) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (e) => {
          fileInfo.url = reader.result;
        };
      }
      this.tmpUploadFiles.push(fileInfo);
    }

    this.showDialog = this.tmpUploadFiles.length > 0;

    // 顯示超過檔案大小的提示訊息
    if (invalidFileInfos.length > 0) {
      const msgs = invalidFileInfos.map((x) => {
        const fullName = `${x.fileName}${x.extension}`;
        const maxSize = this.fileSizePipe.transform(this.maxFileSize, 'M');
        this.logger.debug('maxSize', maxSize);
        const msg = this.translateService.instant(
          'File size exceeds limit ({maxSize})',
          { maxSize }
        );
        const msgItem: MessageItem = {
          category: fullName,
          message: msg,
        };
        return msgItem;
      });
      this.hintDialogInputService.showDataFormatErrorHint(msgs);

      if (this.showDialog === false) {
        this.onUploadDialogCancel();
      }
      event.target.value = '';
    }
  }

  /**
   * 上傳 Dialog 確認事件
   *
   * @memberof FileUploadComponent
   */
  onUploadDialogConfirm(): void {
    const fileNameList = [...this.files, ...this.tmpUploadFiles].map(
      (x) => x.fileName
    );
    const uniqueFileNames = new Set(fileNameList);

    if (
      this.isCheckDuplicateFileName &&
      uniqueFileNames.size !== fileNameList.length &&
      !this.isFormDlw
    ) {
      this.hintDialogInputService.showErrorHint(
        !isNullOrEmpty(this.fileNameDuplicateMsg)
          ? this.fileNameDuplicateMsg
          : 'Library.SupplementRecords.FileNameMustNotBeDuplicate'
      );
      return;
    }

    let checkFileName = false;
    let checkOCRDocumentExtension = false;
    this.logger.debug('checkFile', this.tmpUploadFiles);
    this.tmpUploadFiles.forEach((element) => {
      if (isNullOrEmpty(element.fileName) && !this.isFormDlw) {
        checkFileName = true;
      }

      // 如果是OCR檔案類型 只能上傳特定檔案類型
      if (this.fileService.isOCRDocumentType(element.documentType!, this.caseSubmissionType) && !this.fileService.isOCRDocumentExtension(element.extension!)) {
        checkOCRDocumentExtension = true;
      }

    });

    if (checkFileName) {
      this.hintDialogInputService.showErrorHint(
        'Library.SupplementRecords.FileNameMustNotBeEmpty'
      );
      return;
    }

    if (checkOCRDocumentExtension) {
      console.log('showErrorHint');
      this.hintDialogInputService.showErrorHint(
        'Library.SupplementRecords.OCRDocumentTypeMustSpecificExtension'
      );
      return;
    }


    this.tmpUploadFiles.forEach((x) => this.onRowEditSave(x));
    this.files = this.files.concat(this.tmpUploadFiles);

    this.logger.debug('onUpload', this.files);
    this.filesChange.emit(this.files);
    this.resetTempFiles();
    this.showDialog = false;
    this.displayImages = this.getGalleria();
  }

  /**
   * 上傳 Dialog 取消事件
   *
   * @memberof FileUploadComponent
   */
  onUploadDialogCancel(): void {
    this.resetTempFiles();
  }

  /**
   * 檔案(圖片)列 Click 事件
   *
   * @param {FileDisplayInfo} fileInfo
   * @memberof FileUploadComponent
   */
  onRowImageClick(fileInfo: FileDisplayInfo): void {
    this.showGalleria = true;
    this.currentDisplayImgIdx = 0;
    this.displayImages.forEach((img, index) => {
      if (img.title === fileInfo.fileName) {
        this.currentDisplayImgIdx = index;
      }
    });
  }

  /**
   * 檔案資料表變更事件
   *
   * @param {*} event
   * @memberof FileUploadComponent
   */
  onValueChange(event: any): void {
    this.displayImages = this.getGalleria();
    this.filesChange.emit(event);
  }

  /**
   * 資料列刪除事件
   *
   * @param {any[]} rows
   * @memberof FileUploadComponent
   */
  onRowDelete(rows: any[]): boolean {
    let checker = true;
    const notCreator = rows.some((x) => {
      this.logger.debug('file upload onRowDelete', this.userNo);
      return x.creator ? x.creator != this.userNo : false;
    });
    this.logger.debug('file upload onRowDelete', notCreator);
    if (notCreator) {
      checker = false;
    } else {
      rows.forEach((x) => (x.status = ChangeStatusEnum.deleted));
      this.deletedFiles = this.deletedFiles.concat(rows);
      this.filesDelete.emit(rows);
    }
    return checker;
  }
  onRowDeleteMsg(result: boolean) {
    if (result) {
      this.hintDialogInputService.showErrorHint(
        'Library.SupplementRecords.NotUploaderCantDelete'
      );
    }
  }

  /**
   * 檔案資訊變更事件
   *
   * @param {FileDisplayInfo} fileInfo
   * @memberof FileUploadComponent
   */
  onFileInfoChange(fileInfo: FileDisplayInfo): void {
    // 新增或刪除不可變更狀態
    if (['added', 'deleted'].includes(fileInfo.status) === false) {
      fileInfo.status = ChangeStatusEnum.modified;
    }
    this.filesChange.emit(this.files);
    this.logger.debug('onFileInfoChange', fileInfo, this.files);
  }

  /**
   * 編輯資料列儲存事件
   *
   * @param {FileDisplayInfo} fileInfo
   * @memberof FileUploadComponent
   */
  onRowEditSave(fileInfo: FileDisplayInfo): void {
    if (
      isNullOrEmpty(fileInfo.file) === false &&
      fileInfo.fileName !== fileInfo.file?.name
    ) {
      fileInfo.file = new File(
        [fileInfo.file!],
        `${fileInfo.fileName}${fileInfo.extension}`
      );
      fileInfo.uploadTime = new Date();
      this.logger.log('onRowEditSave', fileInfo);

      // 附上特定種類的檔案描述
      const defaultDescription = this.fileService.generateDefaultDocumentTypeDescription(fileInfo.documentType!, this.caseSubmissionType);
      const needDash = fileInfo.description && defaultDescription;
      fileInfo.description = `${fileInfo.description || ''}${needDash ? '-' : ''}${defaultDescription}`
    }
  }

  /**
   * 依副檔名取得顯示的 icon
   *
   * @param {FileInfo} fileInfo
   * @return {*}  {string}
   * @memberof FileUploadComponent
   */
  getFileTypeIconStyleClassName(fileInfo: FileInfo): string {
    const category = fileInfo.extension?.replace('.', '').toLowerCase();
    if (isNullOrEmpty(category)) {
      return '';
    }
    return FILE_EXT_ICON_MAP.get(category!) ?? '';
  }

  /**
   * 是否為檔案類型 icon
   *
   * @param {FileInfo} fileInfo
   * @return {*}  {boolean}
   * @memberof FileUploadComponent
   */
  checkIsShowFileTypeIcon(fileInfo: FileInfo): boolean {
    const category = fileInfo.extension?.replace('.', '').toLowerCase();
    if (isNullOrEmpty(category)) {
      return false;
    }
    return FILE_EXT_ICON_MAP.has(category!);
  }

  /**
   * 是否為圖片類型 icon
   *
   * @param {FileInfo} fileInfo
   * @return {*}  {boolean}
   * @memberof FileUploadComponent
   */
  checkIsPhoto(fileInfo: FileInfo): boolean {
    const category = fileInfo.extension?.replace('.', '').toLowerCase();
    if (isNullOrEmpty(category)) {
      return false;
    }
    return IMG_EXT_ICON_MAP.has(category!);
  }

  /**取得圖片檔案url */
  getFileUrl(fileId: any): any {
    let url = this.fileTransferService.getFileCenterDownloadUrl(fileId);
    return url;
  }

  /**
   * 取得 Gallery 列表
   *
   * @return {*}  {ImageRawData[]}
   * @memberof FileUploadComponent
   */
  getGalleria(): ImageRawData[] {
    const tmpImages: ImageRawData[] = [];
    this.files.forEach((file) => {
      const extName = file.extension?.replace('.', '').toLowerCase() ?? '';
      if (['png', 'jpg', 'gif', 'jpeg'].includes(extName)) {
        const imageObj: ImageRawData = {
          previewImageSrc: file.url,
          thumbnailImageSrc: file.url,
          alt: file.description,
          title: file.fileName,
        };
        tmpImages.push(imageObj);
      }
    });
    return tmpImages;
  }

  /**
   * 下載檔案
   *
   * @param {FileDisplayInfo} fileInfo
   * @memberof FileUploadComponent
   */
  downloadFile(fileInfo: FileDisplayInfo): void {
    if (isNullOrEmpty(fileInfo.file) === false && fileInfo.isTemp) {
      this.fileTransferService.downloadTempFile(fileInfo);
    } else {
      const fullName = `${fileInfo.fileName}${fileInfo.extension}`;
      this.fileTransferService.downloadFileFromFileCenter(
        fileInfo.fileId!,
        fullName
      );
    }
  }

  /**
   * 取得顯示檔案名稱
   *
   * @param {FileDisplayInfo} fileInfo
   * @return {*}  {string}
   * @memberof FileUploadComponent
   */
  getDisplayFileName(fileInfo: FileDisplayInfo): string {
    return `${fileInfo.fileName}${fileInfo.extension}`;
  }

  /**
   * 同步至檔案中心
   *
   * @param {FileSyncData} syncData
   * @return {*}  {Observable<any>}
   * @memberof FileUploadComponent
   */
  syncToFileCenter(syncData: FileSyncData): Observable<any> {
    const tasks: any[] = [];
    if (syncData.newFiles.length > 0) {
      tasks.push(
        this.fileTransferService.uploadFileToFileCenter(
          syncData.domain,
          syncData.subDomain,
          syncData.newFiles
        )
      );
    }
    if (syncData.updateFiles.length > 0) {
      tasks.push(
        this.fileTransferService
          .updateFileToFileCenter(syncData.updateFiles)
          .pipe(map((result) => []))
      );
    }
    if (syncData.deleteFiles.length > 0) {
      const filesToBeDeleted = [] as FileDisplayInfo[];
      syncData.deleteFiles.forEach(async (file) => {
        if (!isNullOrEmpty(file.fileId)) {
          const fileDetail = await this.fileTransferService.getCurrentFile(
            file.fileId!
          );
          if (!isNullOrEmpty(fileDetail)) filesToBeDeleted.push(file);
        }
      });
      if (filesToBeDeleted.length > 0) {
        tasks.push(
          this.fileTransferService
            .deleteFileInFileCenter(filesToBeDeleted)
            .pipe(map((result) => []))
        );
      }
    }
    const observable =
      tasks.length > 0
        ? combineLatest(tasks).pipe(
          map<any[], any[]>((results) =>
            results.reduce((acc, item) => acc.concat(item ?? []), [])
          )
        )
        : new Observable((observer) => {
          observer.next([]);
          observer.complete();
        });

    return observable;
  }

  /**
   * 同步至檔案中心(簡易版)
   *
   * @param {string} domain
   * @param {string} subDomain
   * @return {*}  {Observable<any>}
   * @memberof FileUploadComponent
   */
  easySyncToFileCenter(domain: string, subDomain: string): Observable<any> {
    const syncData: FileSyncData = {
      domain,
      subDomain,
      newFiles: this.files.filter((x) => x.status === ChangeStatusEnum.added),
      updateFiles: this.files.filter(
        (x) => x.status === ChangeStatusEnum.modified
      ),
      deleteFiles: this.deletedFiles.filter((x) => !x.isTemp),
    };

    return this.syncToFileCenter(syncData);
  }

  /**
   * 重置 HTML File Input
   *
   * @private
   * @memberof FileUploadComponent
   */
  private resetTempFiles(): void {
    this.tmpUploadFiles = [];
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = null;
    }
  }

  /**
   * 初始化 Form Group
   *
   * @private
   * @memberof FileUploadComponent
   */
  private initFormGroup(): void {
    const ctrls = this.files.map(
      (x) =>
        new FormControl('description', [
          Validators.required,
          ValidatorsPlus.maxLength(100),
        ])
    );
    this.form = this.fb.group({ files: this.fb.array(ctrls) });
  }
  pageActions = {
    /**
     * 資料列刪除事件
     *
     * @param {any[]} rows
     * @memberof FileUploadComponent
     */
    onRowDelete: (rows: any[]): boolean => {
      let checker = true;
      const notCreator = rows.some((x) => {
        this.logger.debug('file upload onRowDelete', this.userNo);
        return x.creator ? x.creator != this.userNo : false;
      });
      this.logger.debug('file upload onRowDelete', notCreator);
      if (notCreator) {
        checker = false;
      } else {
        rows.forEach((x) => (x.status = ChangeStatusEnum.deleted));
        this.deletedFiles = this.deletedFiles.concat(rows);
        this.filesDelete.emit(rows);
      }
      return checker;
    },
  };
}

/**
 * Gallery 資料結構
 *
 * @export
 * @interface ImageRawData
 */
export interface ImageRawData {
  previewImageSrc?: string | ArrayBuffer | null;
  thumbnailImageSrc?: string | ArrayBuffer | null;
  alt?: string;
  title?: string;
}

/**
 * 檔案資訊資料結構
 *
 * @export
 * @interface FileDisplayInfo
 * @extends {FileInfo}
 */
export interface FileDisplayInfo extends FileInfo {
  id: string;
  url?: string | ArrayBuffer | null;
  //消金欄位
  documentType?: string;
  subKey?: number;
  secSubKey?: number;
  systemFunctionId?: number;
  createTime?: Date;
  attachmentId?: number;
  editing?: boolean;
}

/**
 * 檔案中心同步資料結構
 *
 * @export
 * @interface FileSyncData
 */
export interface FileSyncData {
  domain: string;
  subDomain: string;
  newFiles: FileDisplayInfo[];
  updateFiles: FileDisplayInfo[];
  deleteFiles: FileDisplayInfo[];
}
